/**
 * site-navbar — kontejner primární navigace (vanilla TS, bez závislosti na Bootstrapu)
 * -----------------------------------------------------------------------------
 * Řeší výhradně lištu:
 *  - pozici ve stránce a sticky režim (class .site-navbar--sticky-enabled),
 *  - přilepení přes position:fixed + spacer (class .site-navbar--pinned),
 *  - kompaktní vzhled po odscrollování (class .site-navbar--sticky),
 *  - rozbalovací panel s hamburgerem pod breakpointem.
 *
 * Compact transition (výška, logo, písmo menu) běží najednou. Po dobu animace
 * má lišta class `.site-navbar--compacting` (menu během ní „more“ nepřepočítává);
 * po dojetí vyšle `site-navbar:compact-settled`, aby `site-navigation` přeměřila
 * až podle finálních šířek (bez probliknutí „more“ uprostřed animace).
 *
 * O chování menu (úrovně, „more“ položky, pozicování panelů) se stará
 * samostatná komponenta `site-navigation` — obě jsou nezávislé a nevědí o sobě.
 * Jediná dohoda: kdo obslouží Escape, zavolá `preventDefault()`, aby se stejnou
 * klávesou nezavřel i panel lišty.
 *
 * HTML kontrakt:
 *   [data-site-navbar]            korenový element lišty
 *   [data-site-navbar-burger]     tlačítko rozbalení panelu (mobil)
 *   [data-site-navbar-panel]      rozbalovaný obsah lišty
 *   [data-site-navbar-keep-open]  odkaz v panelu, který panel nezavírá
 *   [data-site-navbar-breakpoint] [px] hranice mobil / lišta (default 768)
 *   [data-site-navbar-sticky-offset] [px] práh kompaktního vzhledu (bez atributu = padding-top body)
 */

export interface SiteNavbarOptions {
    /** korenový element nebo selektor (default: [data-site-navbar]) */
    root?: HTMLElement | string;
    /** [px] pod touto šířkou okna se obsah lišty schovává do panelu */
    breakpoint?: number;
    /** [px] odscrollování, od kterého se zapne kompaktní (sticky) vzhled */
    stickyOffset?: number;
}

const SELECTOR = {
    root: '[data-site-navbar]',
    burger: '[data-site-navbar-burger]',
    panel: '[data-site-navbar-panel]',
    keepOpen: '[data-site-navbar-keep-open]',
};

const CLASS = {
    open: 'is-open',
    ready: 'site-navbar--ready',
    sticky: 'site-navbar--sticky',
    stickyEnabled: 'site-navbar--sticky-enabled',
    pinned: 'site-navbar--pinned',
    compacting: 'site-navbar--compacting',
    spacer: 'site-navbar__spacer',
};

const DEFAULTS = {
    breakpoint: 768,
    stickyOffset: 1,
    compactDurationMs: 280,
};

/** Event na korenovém elementu lišty; `detail.open` = nový stav panelu. */
export const SITE_NAVBAR_PANEL_EVENT = 'site-navbar:panel';

/**
 * Po dojetí compact / expand transition (výška, logo, písmo).
 * `detail.compact` = true, když zůstala sticky/kompaktní.
 * `site-navigation` na něj přepočítá „more“ (během `.site-navbar--compacting` neměří).
 */
export const SITE_NAVBAR_COMPACT_SETTLED_EVENT = 'site-navbar:compact-settled';

const numberFromData = (root: HTMLElement, name: string, fallback: number): number => {
    const raw = root.getAttribute(name);
    if (!raw) {
        return fallback;
    }
    const value = parseInt(raw, 10);

    return isNaN(value) ? fallback : value;
};

const cssDurationToMs = (raw: string, fallback: number): number => {
    const value = raw.trim();
    if (!value) {
        return fallback;
    }
    if (value.endsWith('ms')) {
        const ms = parseFloat(value);

        return Number.isFinite(ms) ? ms : fallback;
    }
    if (value.endsWith('s')) {
        const sec = parseFloat(value);

        return Number.isFinite(sec) ? sec * 1000 : fallback;
    }
    const num = parseFloat(value);

    return Number.isFinite(num) ? num : fallback;
};

export class SiteNavbar {
    private readonly root: HTMLElement;
    private readonly panel: HTMLElement | null;
    private readonly burger: HTMLElement | null;

    private readonly breakpoint: number;
    private readonly stickyOffsetExplicit: number | null;

    private wasMobile: boolean;
    private spacer: HTMLElement | null = null;
    private compactTimer = 0;
    private panelHeightEnd: ((event: TransitionEvent) => void) | null = null;

    constructor(root: HTMLElement, options: SiteNavbarOptions = {}) {
        this.root = root;
        this.panel = root.querySelector<HTMLElement>(SELECTOR.panel);
        this.burger = root.querySelector<HTMLElement>(SELECTOR.burger);

        this.breakpoint =
            options.breakpoint ?? numberFromData(root, 'data-site-navbar-breakpoint', DEFAULTS.breakpoint);
        this.stickyOffsetExplicit =
            options.stickyOffset ??
            (root.hasAttribute('data-site-navbar-sticky-offset')
                ? numberFromData(root, 'data-site-navbar-sticky-offset', DEFAULTS.stickyOffset)
                : null);
        this.wasMobile = this.isMobile();

        this.bind();
        this.root.classList.add(CLASS.ready);
        this.updateSticky();
    }

    destroy = (): void => {
        window.clearTimeout(this.compactTimer);
        this.clearPanelHeightListener();
        if (this.panel) {
            this.panel.style.maxHeight = '';
        }
        this.root.removeEventListener('click', this.onClick);
        this.root.removeEventListener('keydown', this.onKeyDown);
        this.root.removeEventListener(SITE_NAVBAR_PANEL_EVENT, this.onPanelChange);
        document.removeEventListener('click', this.onDocumentClick);
        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('scroll', this.onScroll);
        this.spacer?.remove();
        this.spacer = null;
        this.root.classList.remove(CLASS.ready, CLASS.sticky, CLASS.pinned, CLASS.compacting);
    };

    /** Otevře / zavře panel (mobilní rozbalení obsahu lišty). */
    setPanelOpen = (open: boolean): void => {
        if (!this.panel || open === this.isPanelOpen()) {
            return;
        }

        if (this.isMobile()) {
            this.animatePanelHeight(open);
        } else {
            this.clearPanelHeightListener();
            this.panel.style.maxHeight = '';
            this.panel.classList.toggle(CLASS.open, open);
        }

        this.burger?.setAttribute('aria-expanded', open ? 'true' : 'false');
        this.root.dispatchEvent(new CustomEvent(SITE_NAVBAR_PANEL_EVENT, { detail: { open }, bubbles: true }));
    };

    isPanelOpen = (): boolean => !!this.panel && this.panel.classList.contains(CLASS.open);

    /** Plynulé max-height podle skutečné výšky obsahu (ne skok 0 ↔ 100dvh). */
    private animatePanelHeight(open: boolean): void {
        const panel = this.panel;
        if (!panel) {
            return;
        }

        this.clearPanelHeightListener();

        if (open) {
            panel.classList.add(CLASS.open);
            panel.style.maxHeight = '0px';
            // reflow, ať prohlížeč vezme 0 jako start transition
            void panel.offsetHeight;
            panel.style.maxHeight = `${panel.scrollHeight}px`;

            this.panelHeightEnd = (event: TransitionEvent): void => {
                if (event.propertyName !== 'max-height' || event.target !== panel) {
                    return;
                }
                this.clearPanelHeightListener();
                if (this.isPanelOpen()) {
                    // volný růst obsahu (vnořená submenu) po doběhnutí animace
                    panel.style.maxHeight = '';
                }
            };
            panel.addEventListener('transitionend', this.panelHeightEnd);
        } else {
            panel.style.maxHeight = `${panel.scrollHeight}px`;
            void panel.offsetHeight;
            panel.classList.remove(CLASS.open);
            panel.style.maxHeight = '0px';

            this.panelHeightEnd = (event: TransitionEvent): void => {
                if (event.propertyName !== 'max-height' || event.target !== panel) {
                    return;
                }
                this.clearPanelHeightListener();
                if (!this.isPanelOpen()) {
                    panel.style.maxHeight = '';
                }
            };
            panel.addEventListener('transitionend', this.panelHeightEnd);
        }
    }

    private clearPanelHeightListener(): void {
        if (this.panel && this.panelHeightEnd) {
            this.panel.removeEventListener('transitionend', this.panelHeightEnd);
        }
        this.panelHeightEnd = null;
    }

    private bind(): void {
        this.root.addEventListener('click', this.onClick);
        this.root.addEventListener('keydown', this.onKeyDown);
        this.root.addEventListener(SITE_NAVBAR_PANEL_EVENT, this.onPanelChange);
        document.addEventListener('click', this.onDocumentClick);
        window.addEventListener('resize', this.onResize);
        window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    private onClick = (event: MouseEvent): void => {
        const target = event.target as HTMLElement | null;
        if (!target) {
            return;
        }

        const burger = target.closest<HTMLElement>(SELECTOR.burger);
        if (burger && this.root.contains(burger)) {
            event.preventDefault();
            this.setPanelOpen(!this.isPanelOpen());

            return;
        }

        // odkaz v rozbaleném panelu → po kliknutí panel uklidit (navigace pokračuje)
        const link = target.closest<HTMLAnchorElement>('a[href]');
        if (link && this.panel?.contains(link) && !link.matches(SELECTOR.keepOpen)) {
            this.setPanelOpen(false);
        }
    };

    private onDocumentClick = (event: MouseEvent): void => {
        const target = event.target as Node | null;
        if (target && this.root.contains(target)) {
            return;
        }

        this.setPanelOpen(false);
    };

    private onKeyDown = (event: KeyboardEvent): void => {
        // Escape už mohla obsloužit vnořená komponenta (zavření podmenu)
        if (event.key !== 'Escape' || event.defaultPrevented || !this.isPanelOpen()) {
            return;
        }

        this.setPanelOpen(false);
        this.burger?.focus();
    };

    private onPanelChange = (): void => {
        // během compact transition spacer drží startovní výšku — sync až po settle
        if (this.root.classList.contains(CLASS.compacting)) {
            return;
        }
        this.syncSpacerToCurrentHeight();
    };

    private onScroll = (): void => {
        this.updateSticky();
    };

    private onResize = (): void => {
        const mobile = this.isMobile();
        if (mobile !== this.wasMobile) {
            this.wasMobile = mobile;
            // nad breakpointem je obsah lišty vidět vždy — stav panelu nesmí zůstat „otevřený“
            if (!mobile) {
                this.setPanelOpen(false);
            }
        }

        this.updateSticky();
        if (this.root.classList.contains(CLASS.pinned) && !this.root.classList.contains(CLASS.compacting)) {
            this.syncSpacerToCurrentHeight();
        }
    };

    private updateSticky = (): void => {
        if (!this.root.classList.contains(CLASS.stickyEnabled)) {
            this.releasePin();

            return;
        }

        if (window.scrollY > this.getStickyOffset()) {
            this.applyPin();
        } else {
            this.releasePin();
        }
    };

    /**
     * Kompaktní vzhled až ve chvíli, kdy lišta dojede k horní hraně viewportu.
     * Bez explicitního offsetu se práh bere z `padding-top` na `body` (odsazení stránky).
     */
    private getStickyOffset(): number {
        if (this.stickyOffsetExplicit !== null) {
            return this.stickyOffsetExplicit;
        }

        const inset = Number.parseFloat(getComputedStyle(document.body).paddingTop) || 0;

        return Math.max(DEFAULTS.stickyOffset, inset);
    }

    /** Duration compact transition ze CSS (`--site-navbar-transition-duration`). */
    private getCompactDurationMs(): number {
        return cssDurationToMs(
            getComputedStyle(this.root).getPropertyValue('--site-navbar-transition-duration'),
            DEFAULTS.compactDurationMs,
        );
    }

    /**
     * Drží místo v toku stránky, když je lišta position:fixed.
     * CSS sticky v .example nefunguje — lišta je v krátkém #zacatek (jen výška navbaru),
     * sticky se v takovém parentu neudrží. Výšku spaceru zamknout ještě před fixed
     * (jinak jeden snímek odhodí obsah — OCA-67).
     */
    private ensureSpacer(): HTMLElement {
        if (!this.spacer) {
            this.spacer = document.createElement('div');
            this.spacer.className = CLASS.spacer;
            this.spacer.setAttribute('aria-hidden', 'true');
            this.root.insertAdjacentElement('afterend', this.spacer);
        }

        return this.spacer;
    }

    private setSpacerHeight(height: number, animate = false): void {
        if (height <= 0) {
            if (this.spacer) {
                this.spacer.style.transition = 'none';
                this.spacer.style.display = 'none';
                this.spacer.style.height = '0';
            }

            return;
        }

        const spacer = this.ensureSpacer();
        spacer.style.transition = animate ? '' : 'none';
        spacer.style.display = 'block';
        spacer.style.height = `${height}px`;
        if (!animate) {
            // flush, ať další změna výšky může jet CSS transition
            void spacer.offsetHeight;
        }
    }

    private applyPin(): void {
        if (this.root.classList.contains(CLASS.pinned)) {
            return;
        }

        // spacer = výška před compactem; až po dojetí CSS transition ji stáhneme
        this.setSpacerHeight(this.root.offsetHeight, false);
        // pin + compacting + sticky najednou; „more“ až na compact-settled
        this.root.classList.add(CLASS.compacting, CLASS.pinned, CLASS.sticky);
        this.scheduleCompactSettled(true);
    }

    private releasePin(): void {
        if (!this.root.classList.contains(CLASS.pinned) && !this.root.classList.contains(CLASS.sticky)) {
            return;
        }

        this.root.classList.add(CLASS.compacting);
        this.root.classList.remove(CLASS.sticky, CLASS.pinned);
        this.setSpacerHeight(0);
        this.scheduleCompactSettled(false);
    }

    /**
     * Po dojetí compact/expand animace: sladí spacer (jen při pinu) a vyšle settled
     * (menu přepočítá „more“ podle finálních šířek).
     */
    private scheduleCompactSettled(compact: boolean): void {
        window.clearTimeout(this.compactTimer);

        this.compactTimer = window.setTimeout(() => {
            this.root.classList.remove(CLASS.compacting);
            if (compact && this.root.classList.contains(CLASS.pinned)) {
                this.syncSpacerToCurrentHeight();
            }
            this.root.dispatchEvent(
                new CustomEvent(SITE_NAVBAR_COMPACT_SETTLED_EVENT, {
                    detail: { compact },
                    bubbles: true,
                }),
            );
        }, this.getCompactDurationMs());
    }

    private syncSpacerToCurrentHeight(): void {
        if (!this.root.classList.contains(CLASS.pinned)) {
            this.setSpacerHeight(0);

            return;
        }

        this.setSpacerHeight(this.root.offsetHeight, true);
    }

    private isMobile(): boolean {
        return window.innerWidth < this.breakpoint;
    }
}

/** Inicializace všech lišt na stránce. */
export const initSiteNavbars = (options: SiteNavbarOptions = {}): Array<SiteNavbar> => {
    const roots =
        typeof options.root === 'string'
            ? Array.from(document.querySelectorAll<HTMLElement>(options.root))
            : options.root
              ? [options.root]
              : Array.from(document.querySelectorAll<HTMLElement>(SELECTOR.root));

    return roots.map((root) => new SiteNavbar(root, options));
};
